import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Badge } from '@/components/ui/StatusBadge';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuthStore } from '@/stores/auth';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { cn } from '@/lib/utils';
import {
  configApi,
  type AdminUser,
  type ChecklistTemplate,
  type ContractType,
  type CreatedUserResponse,
  type EmailTemplate,
  type InsuranceCompany,
  type ResetPasswordResponse,
  type Settings,
  type UserRole,
} from '@/services/config.api';
import {
  sequencesApi,
  type Sequence as SequenceSummary,
  type SequenceStepType,
} from '@/services/sequences.api';
import { teamsApi, type TeamSummary } from '@/services/teams.api';
import { EnterpriseTab } from '@/components/EnterpriseTab';
import { ObjectivesTab } from '@/components/ObjectivesTab';
import { ExportTab } from '@/components/ExportTab';

// L'onglet « Séquences » est volontairement masqué en V1 : la fonctionnalité
// existante (module + composants `SequencesTab`/`CreateSequenceModal`/... plus
// bas dans ce fichier) reste en place côté code pour être retravaillée en V2,
// mais n'est pas exposée à la direction. Tant qu'elle n'est pas référencée
// dans `TABS`, la clé ne fait plus partie de `TabKey` et aucun deep-link
// `?tab=sequences` ne l'active (fallback sur « users »).
// Onglet `export` rendu visible uniquement pour le rôle `admin` (cf
// décision produit 2026-04-28). Présent dans l'enum/TabKey pour que les
// deep-links `?tab=export` fonctionnent — la garde RBAC se fait au render.
const TABS = [
  { key: 'users' as const, label: 'Utilisateurs' },
  { key: 'teams' as const, label: 'Équipes' },
  { key: 'enterprise' as const, label: 'Entreprise' },
  { key: 'contract-types' as const, label: 'Types de contrats' },
  { key: 'companies' as const, label: "Compagnies d'assurance" },
  { key: 'objectives' as const, label: 'Objectifs' },
  { key: 'email-templates' as const, label: 'Templates emails' },
  { key: 'settings' as const, label: 'Paramètres' },
  { key: 'export' as const, label: 'Export', adminOnly: true },
];
type TabKey = (typeof TABS)[number]['key'];

const TAB_KEYS = new Set<TabKey>(TABS.map((t) => t.key));
function isTabKey(v: string | null): v is TabKey {
  return !!v && TAB_KEYS.has(v as TabKey);
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  director: 'Directeur',
  manager: 'Manager',
  collaborator: 'Collaborateur',
  executive: 'Dirigeant',
};

const ROLE_TONES: Record<UserRole, Parameters<typeof Badge>[0]['tone']> = {
  admin: 'danger',
  director: 'accent',
  manager: 'warning',
  collaborator: 'neutral',
  executive: 'success',
};

export function ConfigPage() {
  const user = useAuthStore((s) => s.user);
  const isAllowed = user?.role === 'admin' || user?.role === 'director';
  const [searchParams, setSearchParams] = useSearchParams();
  // Hydrate initial tab depuis `?tab=...` pour permettre aux autres pages de
  // deep-linker vers un onglet spécifique (ex : bouton « Gérer les objectifs »
  // du KPI Direction → /config?tab=objectives). Source de vérité = l'URL.
  const initialTab = isTabKey(searchParams.get('tab'))
    ? (searchParams.get('tab') as TabKey)
    : 'users';
  const [tab, setTab] = useState<TabKey>(initialTab);

  // Resync si la query change (navigation back/forward ou deep-link).
  useEffect(() => {
    const q = searchParams.get('tab');
    if (isTabKey(q) && q !== tab) setTab(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function handleChange(next: TabKey) {
    setTab(next);
    // Propage dans l'URL pour que la sélection survive à un reload / partage.
    const params = new URLSearchParams(searchParams);
    if (next === 'users') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  if (!isAllowed) return <Navigate to="/" replace />;

  // Filtre les tabs : `adminOnly` masqué pour les director.
  const visibleTabs = TABS.filter((t) => !t.adminOnly || user?.role === 'admin');

  // Sécurise un deep-link `?tab=export` posé par un director : on retombe
  // sur 'users' silencieusement.
  const safeTab: TabKey = visibleTabs.some((t) => t.key === tab) ? tab : 'users';

  return (
    <AppLayout>
      <PageHeader
        title="Configuration"
        subtitle="Administration du CRM - accès direction uniquement."
      />
      <Tabs tabs={visibleTabs} current={safeTab} onChange={handleChange} />
      {safeTab === 'users' && <UsersTab />}
      {safeTab === 'teams' && <TeamsTab />}
      {safeTab === 'enterprise' && <EnterpriseTab userRole={user?.role} />}
      {safeTab === 'contract-types' && <ContractTypesTab />}
      {safeTab === 'companies' && <CompaniesTab />}
      {safeTab === 'objectives' && <ObjectivesTab />}
      {/* Onglet Séquences masqué V1 - le composant reste référencé ici pour
          que TypeScript ne le marque pas comme unused et que la réactivation
          V2 soit un one-liner (retirer `false && ` + ré-ajouter l'entrée dans TABS). */}
      {/* eslint-disable-next-line no-constant-binary-expression -- pattern intentionnel V1 → V2 documenté ci-dessus (préserve référence TypeScript pour éviter "unused import" sans afficher le composant) */}
      {false && <SequencesTab />}
      {safeTab === 'email-templates' && <EmailTemplatesTab />}
      {safeTab === 'settings' && <SettingsTab />}
      {safeTab === 'export' && user?.role === 'admin' && <ExportTab />}
    </AppLayout>
  );
}

// ===========================================================================
// Onglet Utilisateurs
// ===========================================================================

function UsersTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const currentUserId = useAuthStore((s) => s.user?.id);
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [createdInfo, setCreatedInfo] = useState<CreatedUserResponse | null>(null);
  const [resetInfo, setResetInfo] = useState<ResetPasswordResponse | null>(null);

  async function load() {
    setError(null);
    try {
      setItems(await configApi.listUsers(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggleActive(u: AdminUser) {
    try {
      await configApi.updateUser(token, u.id, { isActive: !u.isActive });
      toast({
        tone: 'success',
        message: u.isActive ? 'Utilisateur désactivé' : 'Utilisateur activé',
      });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  async function resetPassword(u: AdminUser) {
    const ok = await confirm({
      title: `Réinitialiser le mot de passe de ${u.firstName} ${u.lastName} ?`,
      message:
        'Un mot de passe temporaire sera généré. L’utilisateur devra le changer à sa prochaine connexion.',
      variant: 'warning',
      confirmLabel: 'Réinitialiser',
    });
    if (!ok) return;
    try {
      const res = await configApi.resetUserPassword(token, u.id);
      setResetInfo(res);
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const columns: Column<AdminUser>[] = [
    {
      key: 'name',
      header: 'Nom',
      render: (u) => (
        <div>
          <div className="font-medium text-brand">
            {u.firstName} {u.lastName}
          </div>
          <div className="text-xs text-text-muted">{u.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rôle',
      render: (u) => <Badge tone={ROLE_TONES[u.role]}>{ROLE_LABELS[u.role]}</Badge>,
    },
    {
      key: 'team',
      header: 'Équipe',
      render: (u) => u.team?.name ?? <span className="text-text-muted">-</span>,
    },
    {
      key: 'status',
      header: 'Statut',
      render: (u) =>
        u.isActive ? <Badge tone="success">Actif</Badge> : <Badge tone="neutral">Désactivé</Badge>,
    },
    {
      key: 'lastLoginAt',
      header: 'Dernière connexion',
      render: (u) =>
        u.lastLoginAt ? (
          new Date(u.lastLoginAt).toLocaleString('fr-FR')
        ) : (
          <span className="text-text-muted">Jamais</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (u) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(u)}>
            Modifier
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void resetPassword(u)}>
            Reset mdp
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void toggleActive(u)}
            disabled={u.id === currentUserId}
            title={
              u.id === currentUserId
                ? 'Vous ne pouvez pas désactiver votre propre compte'
                : undefined
            }
          >
            {u.isActive ? 'Désactiver' : 'Activer'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Nouvel utilisateur
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={items ?? []}
        keyExtractor={(u) => u.id}
        loading={items === null}
        empty={{ title: 'Aucun utilisateur' }}
      />
      {createOpen && (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={(res) => {
            setCreateOpen(false);
            setCreatedInfo(res);
            void load();
          }}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          currentUserId={currentUserId ?? null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {createdInfo && (
        <TemporaryPasswordModal
          title="Utilisateur créé"
          description={`Compte de ${createdInfo.user.firstName} ${createdInfo.user.lastName} créé. Communiquez-lui ce mot de passe temporaire - il devra le changer à la première connexion.`}
          temporaryPassword={createdInfo.temporaryPassword}
          onClose={() => setCreatedInfo(null)}
        />
      )}
      {resetInfo && (
        <TemporaryPasswordModal
          title="Mot de passe réinitialisé"
          description="Toutes les sessions ont été révoquées. Communiquez ce nouveau mot de passe temporaire."
          temporaryPassword={resetInfo.temporaryPassword}
          onClose={() => setResetInfo(null)}
        />
      )}
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (res: CreatedUserResponse) => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('collaborator');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charge la liste des équipes au mount pour alimenter le dropdown.
  useEffect(() => {
    let cancelled = false;
    teamsApi
      .list(token)
      .then((list) => {
        if (!cancelled) setTeams(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await configApi.createUser(token, {
        email,
        firstName,
        lastName,
        role,
        teamId,
      });
      onCreated(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  // Manager + collaborator DOIVENT être rattachés à une équipe (sinon ils ne
  // peuvent rien créer côté prospects/opps - le scope filter rejette). Pour
  // admin/director/executive, l'équipe est facultative.
  const teamRequired = role === 'manager' || role === 'collaborator';

  return (
    <Modal onClose={onClose} title="Créer un utilisateur">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="cu-firstName" required>
              Prénom
            </Label>
            <Input
              id="cu-firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="cu-lastName" required>
              Nom
            </Label>
            <Input
              id="cu-lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="cu-email" required>
            Email
          </Label>
          <Input
            id="cu-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="cu-role" required>
              Rôle
            </Label>
            <Select id="cu-role" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="cu-team" required={teamRequired}>
              Équipe
            </Label>
            <Select
              id="cu-team"
              value={teamId ?? ''}
              onChange={(e) => setTeamId(e.target.value === '' ? null : e.target.value)}
              required={teamRequired}
            >
              <option value="">{teamRequired ? '— Sélectionner —' : '— Aucune —'}</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            {teamRequired && !teamId && (
              <p className="mt-1 text-xs text-text-muted">
                Obligatoire pour les rôles Manager et Collaborateur.
              </p>
            )}
          </div>
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            disabled={teamRequired && !teamId}
          >
            Créer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  currentUserId,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  currentUserId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [role, setRole] = useState<UserRole>(user.role);
  const [teamId, setTeamId] = useState<string | null>(user.teamId);
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSelf = currentUserId === user.id;
  const teamRequired = role === 'manager' || role === 'collaborator';

  // Charge les équipes au mount.
  useEffect(() => {
    let cancelled = false;
    teamsApi
      .list(token)
      .then((list) => {
        if (!cancelled) setTeams(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const patch: { role?: UserRole; teamId?: string | null } = {};
      if (role !== user.role) patch.role = role;
      if (teamId !== user.teamId) patch.teamId = teamId;
      if (Object.keys(patch).length === 0) {
        onSaved();
        return;
      }
      await configApi.updateUser(token, user.id, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  const dirty = role !== user.role || teamId !== user.teamId;

  return (
    <Modal onClose={onClose} title={`Modifier ${user.firstName} ${user.lastName}`}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="eu-role">Rôle</Label>
            <Select
              id="eu-role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              disabled={isSelf}
            >
              {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
            {isSelf && (
              <p className="mt-1 text-xs text-text-muted">
                Vous ne pouvez pas modifier votre propre rôle.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="eu-team" required={teamRequired}>
              Équipe
            </Label>
            <Select
              id="eu-team"
              value={teamId ?? ''}
              onChange={(e) => setTeamId(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">{teamRequired ? '— Sélectionner —' : '— Aucune —'}</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            {teamRequired && !teamId && (
              <p className="mt-1 text-xs text-text-muted">
                Obligatoire pour Manager et Collaborateur.
              </p>
            )}
          </div>
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            disabled={!dirty || (teamRequired && !teamId)}
          >
            Enregistrer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function TemporaryPasswordModal({
  title,
  description,
  temporaryPassword,
  onClose,
}: {
  title: string;
  description: string;
  temporaryPassword: string;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-text-muted">{description}</p>
        <div className="rounded-md border border-border-subtle bg-gray-50 p-3 text-center font-mono text-sm text-brand">
          {temporaryPassword}
        </div>
        <Banner tone="warning">
          Ce mot de passe ne sera plus affiché ensuite. Copiez-le maintenant.
        </Banner>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => void navigator.clipboard?.writeText(temporaryPassword)}
          >
            Copier
          </Button>
          <Button variant="primary" onClick={onClose}>
            OK
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Onglet Types de contrats
// ===========================================================================

function ContractTypesTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const [items, setItems] = useState<ContractType[] | null>(null);
  // Préview de checklist par contractTypeId - chargée en parallèle du listing
  // pour afficher un résumé sur chaque ligne ("3 docs : Kbis, CNI, RIB...").
  const [checklistsByCt, setChecklistsByCt] = useState<Record<string, ChecklistTemplate[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ContractType | null>(null);

  async function load() {
    try {
      const cts = await configApi.listContractTypes(token);
      setItems(cts);
      // Chargement parallèle des checklists de tous les types actifs. Les
      // erreurs individuelles sont tolérées (type sans checklist = préview
      // vide), pas bloquantes pour l'affichage du listing.
      const results = await Promise.allSettled(
        cts.map((c) => configApi.listChecklistTemplates(token, c.id)),
      );
      const map: Record<string, ChecklistTemplate[]> = {};
      cts.forEach((c, i) => {
        const r = results[i];
        if (r?.status === 'fulfilled') map[c.id] = r.value;
        else map[c.id] = [];
      });
      setChecklistsByCt(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: Column<ContractType>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (c) => <span className="font-mono text-brand">{c.code}</span>,
    },
    { key: 'label', header: 'Libellé' },
    {
      key: 'applicability',
      header: 'Applicable à',
      render: (c) => (
        <div className="flex gap-1">
          {c.applicableToCompany && <Badge tone="neutral">Entreprises</Badge>}
          {c.applicableToIndividual && <Badge tone="purple">Particuliers</Badge>}
        </div>
      ),
    },
    {
      key: 'checklist',
      header: 'Checklist documentaire',
      render: (c) => (
        <ChecklistPreview items={checklistsByCt[c.id] ?? []} onOpen={() => setEditing(c)} />
      ),
    },
    {
      key: 'isActive',
      header: 'Statut',
      render: (c) =>
        c.isActive ? <Badge tone="success">Actif</Badge> : <Badge tone="neutral">Désactivé</Badge>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (c) => (
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Pencil className="h-3.5 w-3.5" />}
          onClick={() => setEditing(c)}
        >
          Modifier
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Nouveau type
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={items ?? []}
        keyExtractor={(c) => c.id}
        loading={items === null}
        empty={{ title: 'Aucun type de contrat' }}
      />
      {createOpen && (
        <CreateContractTypeModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}
      {editing && (
        <EditContractTypeModal
          contractType={editing}
          onClose={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Résumé de checklist affiché dans le DataTable - tronque à 3 noms + « +N »
 * pour ne pas surcharger la ligne. Clic n'importe où sur la zone = ouvre la
 * modale d'édition.
 */
function ChecklistPreview({ items, onOpen }: { items: ChecklistTemplate[]; onOpen: () => void }) {
  if (items.length === 0) {
    return (
      <button type="button" onClick={onOpen} className="text-xs text-accent hover:underline">
        Aucun document · configurer
      </button>
    );
  }
  const MAX = 3;
  const preview = items
    .slice(0, MAX)
    .map((i) => i.documentName)
    .join(', ');
  const rest = items.length - MAX;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left text-xs text-text-primary hover:text-accent"
      title="Modifier la checklist"
    >
      <span className="font-medium">
        {items.length} document{items.length > 1 ? 's' : ''}
      </span>
      <span className="text-text-muted"> · {preview}</span>
      {rest > 0 && <span className="text-text-muted"> +{rest}</span>}
    </button>
  );
}

function CreateContractTypeModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await configApi.createContractType(token, { code: code.toUpperCase(), label });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Ajouter un type de contrat">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="ct-code" required>
            Code (majuscules)
          </Label>
          <Input
            id="ct-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={20}
            required
          />
        </div>
        <div>
          <Label htmlFor="ct-label" required>
            Libellé
          </Label>
          <Input id="ct-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Ajouter
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Onglet Compagnies
// ===========================================================================

function CompaniesTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [items, setItems] = useState<InsuranceCompany[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function load() {
    try {
      setItems(await configApi.listInsuranceCompanies(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(c: InsuranceCompany) {
    try {
      await configApi.updateInsuranceCompany(token, c.id, { isActive: !c.isActive });
      toast({
        tone: 'success',
        message: c.isActive ? 'Compagnie désactivée' : 'Compagnie activée',
      });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const columns: Column<InsuranceCompany>[] = [
    { key: 'name', header: 'Nom' },
    {
      key: 'isActive',
      header: 'Statut',
      render: (c) =>
        c.isActive ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Désactivée</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (c) => (
        <Button variant="ghost" size="sm" onClick={() => void toggle(c)}>
          {c.isActive ? 'Désactiver' : 'Activer'}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Nouvelle compagnie
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={items ?? []}
        keyExtractor={(c) => c.id}
        loading={items === null}
        empty={{ title: 'Aucune compagnie' }}
      />
      {createOpen && (
        <CreateCompanyModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function CreateCompanyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await configApi.createInsuranceCompany(token, { name });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Ajouter une compagnie">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="ic-name" required>
            Nom
          </Label>
          <Input id="ic-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Ajouter
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Modale d'édition d'un type de contrat (label + statut + checklist intégrée)
// ===========================================================================

function EditContractTypeModal({
  contractType,
  onClose,
}: {
  contractType: ContractType;
  onClose: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [label, setLabel] = useState(contractType.label);
  const [isActive, setIsActive] = useState(contractType.isActive);
  const [applicableToCompany, setApplicableToCompany] = useState(contractType.applicableToCompany);
  const [applicableToIndividual, setApplicableToIndividual] = useState(
    contractType.applicableToIndividual,
  );
  const [items, setItems] = useState<ChecklistTemplate[] | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadChecklist() {
    try {
      setItems(await configApi.listChecklistTemplates(token, contractType.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractType.id]);

  async function handleDeleteItem(item: ChecklistTemplate) {
    const ok = await confirm({
      title: `Supprimer « ${item.documentName} » ?`,
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await configApi.deleteChecklistTemplate(token, item.id);
      toast({ tone: 'success', message: 'Item supprimé' });
      await loadChecklist();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  async function handleToggleRequired(item: ChecklistTemplate) {
    try {
      await configApi.updateChecklistTemplate(token, item.id, { isRequired: !item.isRequired });
      await loadChecklist();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  async function handleSave() {
    if (!applicableToCompany && !applicableToIndividual) {
      setError('Un type de contrat doit être applicable au moins à un type de prospect.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const changes: Parameters<typeof configApi.updateContractType>[2] = {};
      if (label.trim() !== contractType.label) changes.label = label.trim();
      if (isActive !== contractType.isActive) changes.isActive = isActive;
      if (applicableToCompany !== contractType.applicableToCompany)
        changes.applicableToCompany = applicableToCompany;
      if (applicableToIndividual !== contractType.applicableToIndividual)
        changes.applicableToIndividual = applicableToIndividual;
      if (Object.keys(changes).length > 0) {
        await configApi.updateContractType(token, contractType.id, changes);
      }
      toast({ tone: 'success', message: 'Type de contrat enregistré' });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={`Modifier · ${contractType.code} ${contractType.label}`} wide>
      <div className="space-y-5">
        <div>
          <Label htmlFor="ct-edit-label">Libellé</Label>
          <Input
            id="ct-edit-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={100}
          />
        </div>

        <div>
          <Label>Statut</Label>
          <div className="border-border-subtle inline-flex overflow-hidden rounded-md border bg-white">
            {(
              [
                { v: true, l: 'Actif' },
                { v: false, l: 'Désactivé' },
              ] as const
            ).map((opt) => (
              <button
                key={String(opt.v)}
                type="button"
                onClick={() => setIsActive(opt.v)}
                className={cn(
                  'px-4 py-1.5 text-xs font-medium transition-colors',
                  isActive === opt.v
                    ? 'bg-accent/10 text-accent'
                    : 'text-text-muted hover:text-brand',
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Applicable à</Label>
          <div className="mt-1.5 space-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-brand">
              <input
                type="checkbox"
                checked={applicableToCompany}
                onChange={(e) => setApplicableToCompany(e.target.checked)}
                className="h-4 w-4 rounded border-border text-accent focus:ring-accent/30"
              />
              <span>Entreprises</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-brand">
              <input
                type="checkbox"
                checked={applicableToIndividual}
                onChange={(e) => setApplicableToIndividual(e.target.checked)}
                className="h-4 w-4 rounded border-border text-accent focus:ring-accent/30"
              />
              <span>Particuliers</span>
            </label>
          </div>
          <p className="mt-1 text-[11px] text-text-muted">
            Au moins une case doit être cochée. Le type n&apos;apparaîtra dans le dropdown de
            création d&apos;opportunité que pour le(s) type(s) de prospect cochés.
          </p>
        </div>

        <div className="border-t border-border-subtle pt-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Checklist documentaire
              </h3>
              <p className="mt-1 text-[11px] text-text-muted">
                Documents attendus pour les opportunités de ce type. Alimente le scoring de
                complétude.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setAddOpen(true)}
            >
              Ajouter
            </Button>
          </div>

          {items === null && <Skeleton className="mt-3 h-24 w-full" />}
          {items && items.length === 0 && (
            <div className="mt-3 rounded-md border border-dashed border-border-subtle p-4 text-center text-xs text-text-muted">
              Aucun document requis. Cliquez « Ajouter » pour configurer la checklist.
            </div>
          )}
          {items && items.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center gap-3 rounded-md border border-border-subtle bg-white px-3 py-2 text-sm"
                >
                  <span className="w-6 font-mono text-xs text-text-muted">{item.sortOrder}</span>
                  <span className="flex-1 text-brand">{item.documentName}</span>
                  <button
                    type="button"
                    onClick={() => void handleToggleRequired(item)}
                    title="Cliquer pour basculer Obligatoire / Optionnel"
                    className="cursor-pointer"
                  >
                    {item.isRequired ? (
                      <Badge tone="danger">Obligatoire</Badge>
                    ) : (
                      <Badge tone="neutral">Optionnel</Badge>
                    )}
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                    onClick={() => void handleDeleteItem(item)}
                  >
                    Supprimer
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <Banner tone="danger">{error}</Banner>}

        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose}>
            Fermer
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Enregistrer les modifications
          </Button>
        </ModalFooter>
      </div>

      {addOpen && (
        <AddChecklistItemModal
          contractTypeId={contractType.id}
          nextOrder={items?.length ?? 0}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            void loadChecklist();
          }}
        />
      )}
    </Modal>
  );
}

function AddChecklistItemModal({
  contractTypeId,
  nextOrder,
  onClose,
  onCreated,
}: {
  contractTypeId: string;
  nextOrder: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [documentName, setDocumentName] = useState('');
  const [isRequired, setIsRequired] = useState(true);
  const [sortOrder, setSortOrder] = useState<number>(nextOrder);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await configApi.createChecklistTemplate(token, {
        contractTypeId,
        documentName,
        isRequired,
        sortOrder,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Ajouter un item à la checklist">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="cl-name" required>
            Nom du document
          </Label>
          <Input
            id="cl-name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="cl-order">Ordre</Label>
            <Input
              id="cl-order"
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="cl-req">Obligatoire</Label>
            <Select
              id="cl-req"
              value={isRequired ? 'true' : 'false'}
              onChange={(e) => setIsRequired(e.target.value === 'true')}
            >
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </Select>
          </div>
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Ajouter
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Onglet Templates emails
// ===========================================================================

const EMAIL_CATEGORY_LABELS: Record<EmailTemplate['category'], string> = {
  quote_send: 'Envoi devis',
  quote_followup: 'Relance devis',
  document_request: 'Demande pièces',
  meeting_confirm: 'Confirmation RDV',
  signature_thanks: 'Remerciement signature',
  other: 'Autre',
};

function EmailTemplatesTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<EmailTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function load() {
    try {
      setItems(await configApi.listEmailTemplates(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function remove(t: EmailTemplate) {
    const ok = await confirm({
      title: `Supprimer le template « ${t.name} » ?`,
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await configApi.deleteEmailTemplate(token, t.id);
      toast({ tone: 'success', message: 'Template supprimé' });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Nouveau template
        </Button>
      </div>
      {items === null ? (
        <Card>Chargement…</Card>
      ) : items.length === 0 ? (
        <EmptyState variant="list" title="Aucun template" />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((t) => (
            <Card
              key={t.id}
              className="cursor-pointer transition-shadow hover:shadow"
              onClick={() => setSelected(t)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle>{t.name}</CardTitle>
                  <p className="mt-1 truncate text-xs text-text-muted">{t.subject}</p>
                  <div className="mt-2 flex gap-2">
                    <Badge tone="accent">{EMAIL_CATEGORY_LABELS[t.category]}</Badge>
                    {!t.isActive && <Badge tone="neutral">Désactivé</Badge>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(t);
                  }}
                  className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-danger"
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {createOpen && (
        <EmailTemplateEditor
          template={null}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}
      {selected && (
        <EmailTemplateEditor
          template={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

const SUPPORTED_VARIABLES = [
  'prospect.raisonSociale',
  'contact.prenom',
  'contact.nom',
  'opportunity.typeContrat',
  'opportunity.reference',
  'user.prenom',
  'user.nom',
];

function EmailTemplateEditor({
  template,
  onClose,
  onSaved,
}: {
  template: EmailTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [category, setCategory] = useState<EmailTemplate['category']>(
    template?.category ?? 'other',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [focusOn, setFocusOn] = useState<'subject' | 'body'>('body');

  function insertVariable(v: string) {
    const token = `{${v}}`;
    if (focusOn === 'subject') {
      setSubject((s) => s + token);
      subjectRef.current?.focus();
    } else {
      setBody((s) => s + token);
      bodyRef.current?.focus();
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (template) {
        await configApi.updateEmailTemplate(token, template.id, {
          name,
          subject,
          body,
          category,
        });
      } else {
        await configApi.createEmailTemplate(token, { name, subject, body, category });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  async function handlePreview() {
    if (!template) {
      // Créer en mémoire uniquement - la preview nécessite un template en BDD.
      setError('Enregistrez le template pour pouvoir le prévisualiser.');
      return;
    }
    try {
      const res = await configApi.previewEmailTemplate(token, template.id);
      setPreview({ subject: res.subject, body: res.body });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={template ? `Template : ${template.name}` : 'Nouveau template'}
      wide
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="et-name" required>
              Nom
            </Label>
            <Input id="et-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="et-category">Catégorie</Label>
            <Select
              id="et-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as EmailTemplate['category'])}
            >
              {(Object.keys(EMAIL_CATEGORY_LABELS) as EmailTemplate['category'][]).map((c) => (
                <option key={c} value={c}>
                  {EMAIL_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="et-subject" required>
            Objet
          </Label>
          <Input
            id="et-subject"
            ref={subjectRef}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onFocus={() => setFocusOn('subject')}
            required
          />
        </div>
        <div>
          <Label htmlFor="et-body" required>
            Corps
          </Label>
          <Textarea
            id="et-body"
            ref={bodyRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onFocus={() => setFocusOn('body')}
            rows={10}
            required
          />
        </div>
        <div>
          <Label>Variables disponibles (clic pour insérer)</Label>
          <div className="flex flex-wrap gap-2">
            {SUPPORTED_VARIABLES.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => insertVariable(v)}
                className="rounded-md border border-border-subtle bg-white px-2 py-1 font-mono text-xs text-accent transition-colors hover:bg-accent/10"
              >
                {`{${v}}`}
              </button>
            ))}
          </div>
        </div>
        {preview && (
          <Card className="bg-gray-50">
            <CardTitle>Aperçu</CardTitle>
            <div className="mt-2 text-sm">
              <div className="font-medium text-brand">Objet : {preview.subject}</div>
              <pre className="mt-2 whitespace-pre-wrap text-text-primary">{preview.body}</pre>
            </div>
          </Card>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="secondary" type="button" onClick={() => void handlePreview()}>
            Prévisualiser
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Enregistrer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Onglet Paramètres
// ===========================================================================

function SettingsTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  // Le toggle "Mode démonstration" est réservé aux admins protégés
  // (cf SECURITY.md §21 + décision produit 2026-04-28). Un admin standard
  // (ex: naieva.rodot) voit la card en lecture seule sans bouton — évite
  // qu'il active le bandeau démo en prod par mégarde.
  const isProtectedAdmin = useAuthStore((s) => s.user?.isProtectedAdmin === true);
  const toast = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [threshold, setThreshold] = useState<string>('');
  const [demoMode, setDemoMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const s = await configApi.getSettings(token);
      setSettings(s);
      const v = s['commission.notification_threshold'];
      if (typeof v === 'number') setThreshold(String(v));
      setDemoMode(s['demo_mode.enabled'] === true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitThreshold(e: React.FormEvent) {
    e.preventDefault();
    const parsed = Number(threshold);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Valeur numérique strictement positive requise');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await configApi.updateSettings(token, { 'commission.notification_threshold': parsed });
      toast({ tone: 'success', message: 'Paramètres enregistrés' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleDemoMode() {
    try {
      await configApi.updateSettings(token, { 'demo_mode.enabled': !demoMode });
      toast({
        tone: 'success',
        message: !demoMode ? 'Mode démonstration activé' : 'Mode démonstration désactivé',
      });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const currentValue = useMemo(() => {
    const v = settings?.['commission.notification_threshold'];
    return typeof v === 'number' ? v : null;
  }, [settings]);

  return (
    <div className="space-y-4">
      <Card>
        <CardTitle>Paramètres globaux</CardTitle>
        {error && (
          <div className="mt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}
        <form onSubmit={submitThreshold} className="mt-4 max-w-md space-y-4">
          <div>
            <Label htmlFor="st-threshold">Seuil commission - notification direction (€ HT)</Label>
            <Input
              id="st-threshold"
              type="number"
              min={1}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="5000"
            />
            <p className="mt-1 text-xs text-text-muted">
              Valeur actuelle :{' '}
              {currentValue === null ? 'non défini (défaut 5000 €)' : `${currentValue} €`}
            </p>
          </div>
          <Button variant="primary" type="submit" loading={submitting}>
            Enregistrer
          </Button>
        </form>
      </Card>

      <Card>
        <CardTitle>Mode démonstration</CardTitle>
        <p className="mt-2 text-sm text-text-muted">
          Active un bandeau visuel sur toutes les pages pour signaler que l&apos;environnement
          utilise des données fictives. Utilisé pour les démos commerciales, la formation, ou un
          environnement de recette.
        </p>
        <div className="mt-4 flex items-center gap-3">
          {isProtectedAdmin ? (
            <Button variant={demoMode ? 'danger' : 'primary'} onClick={() => void toggleDemoMode()}>
              {demoMode ? 'Désactiver le mode démo' : 'Activer le mode démo'}
            </Button>
          ) : (
            <span className="text-xs italic text-text-muted">
              Toggle réservé à l&apos;administrateur technique du CRM.
            </span>
          )}
          <span className="text-sm">
            Statut actuel :{' '}
            <span className={demoMode ? 'font-semibold text-warning-600' : 'text-text-muted'}>
              {demoMode ? 'Activé' : 'Désactivé'}
            </span>
          </span>
        </div>
      </Card>
    </div>
  );
}

// ===========================================================================
// Onglet Équipes (admin + director)
// ===========================================================================

function TeamsTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<TeamSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TeamSummary | null>(null);

  async function load() {
    setError(null);
    try {
      setItems(await teamsApi.list(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function remove(t: TeamSummary) {
    const ok = await confirm({
      title: `Supprimer l'équipe "${t.name}" ?`,
      message:
        "Cette action est définitive. Une équipe ne peut être supprimée que si aucun utilisateur ni prospect n'y est rattaché.",
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await teamsApi.delete(token, t.id);
      toast({ tone: 'success', message: `Équipe "${t.name}" supprimée` });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const columns: Column<TeamSummary>[] = [
    {
      key: 'name',
      header: 'Nom',
      render: (t) => <span className="font-medium text-brand">{t.name}</span>,
    },
    {
      key: 'manager',
      header: 'Manager',
      render: (t) =>
        t.manager ? (
          `${t.manager.firstName} ${t.manager.lastName}`
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: 'members',
      header: 'Membres',
      render: (t) => <span className="font-mono text-sm">{t._count.members}</span>,
    },
    {
      key: 'prospects',
      header: 'Prospects',
      render: (t) => <span className="font-mono text-sm">{t._count.prospects}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (t) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Pencil className="h-3.5 w-3.5" />}
            onClick={() => setEditing(t)}
          >
            Modifier
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
            onClick={() => void remove(t)}
          >
            Supprimer
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Nouvelle équipe
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={items ?? []}
        keyExtractor={(t) => t.id}
        loading={items === null}
        empty={{
          title: 'Aucune équipe',
          description: 'Créez une équipe pour pouvoir y rattacher des utilisateurs.',
        }}
      />
      {createOpen && (
        <CreateTeamModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}
      {editing && (
        <EditTeamModal
          team={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function CreateTeamModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await teamsApi.create(token, { name: name.trim() });
      toast({ tone: 'success', message: 'Équipe créée' });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Créer une équipe">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="ct-name" required>
            Nom de l&apos;équipe
          </Label>
          <Input
            id="ct-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={100}
            autoFocus
          />
          <p className="mt-1 text-xs text-text-muted">
            Le manager sera désigné après avoir affecté au moins un utilisateur à l&apos;équipe.
          </p>
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!name.trim()}>
            Créer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function EditTeamModal({
  team,
  onClose,
  onSaved,
}: {
  team: TeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [name, setName] = useState(team.name);
  const [managerId, setManagerId] = useState<string | null>(team.managerId);
  const [members, setMembers] = useState<AdminUser[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingMembers(true);
    configApi
      .listUsers(token)
      .then((all) => {
        if (cancelled) return;
        // Manager candidates : membres actifs de cette équipe uniquement.
        // Cohérent avec la règle backend (BUSINESS_RULE_ERROR si on tente
        // d'assigner un user hors de l'équipe).
        setMembers(all.filter((u) => u.teamId === team.id && u.isActive));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur');
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, team.id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const patch: { name?: string; managerId?: string | null } = {};
      if (name.trim() !== team.name) patch.name = name.trim();
      if (managerId !== team.managerId) patch.managerId = managerId;
      if (Object.keys(patch).length === 0) {
        onSaved();
        return;
      }
      await teamsApi.update(token, team.id, patch);
      toast({ tone: 'success', message: 'Équipe mise à jour' });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title={`Modifier l'équipe "${team.name}"`}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="et-name" required>
            Nom
          </Label>
          <Input
            id="et-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={100}
          />
        </div>
        <div>
          <Label htmlFor="et-manager">Manager</Label>
          <Select
            id="et-manager"
            value={managerId ?? ''}
            onChange={(e) => setManagerId(e.target.value === '' ? null : e.target.value)}
            disabled={loadingMembers}
          >
            <option value="">— Aucun —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </Select>
          {!loadingMembers && members.length === 0 && (
            <p className="mt-1 text-xs text-text-muted">
              Aucun utilisateur actif rattaché à cette équipe. Affectez d&apos;abord des membres
              pour pouvoir désigner un manager.
            </p>
          )}
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Enregistrer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Modal helper
// ===========================================================================

function Modal({
  onClose,
  title,
  children,
  wide,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`w-full rounded-lg bg-white shadow-modal animate-scale-in ${wide ? 'max-w-3xl' : 'max-w-md'}`}
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <h2 className="text-lg font-semibold text-brand">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">{children}</div>
  );
}

// ===========================================================================
// Onglet Séquences (V1 light)
// ===========================================================================

function SequencesTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [sequences, setSequences] = useState<SequenceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SequenceSummary | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function load() {
    try {
      setSequences(await sequencesApi.list(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(s: SequenceSummary) {
    try {
      await sequencesApi.update(token, s.id, { isActive: !s.isActive });
      toast({ tone: 'success', message: s.isActive ? 'Séquence désactivée' : 'Séquence activée' });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const columns: Column<SequenceSummary>[] = [
    { key: 'name', header: 'Nom' },
    {
      key: 'steps',
      header: 'Étapes',
      align: 'center',
      render: (s) => (s._count?.enrollments !== undefined ? `${s.steps?.length ?? 0}` : '-'),
    },
    {
      key: 'enrollments',
      header: 'Inscriptions',
      align: 'center',
      render: (s) => s._count?.enrollments ?? 0,
    },
    {
      key: 'isActive',
      header: 'Statut',
      render: (s) =>
        s.isActive ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Désactivée</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (s) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setSelected(s)}>
            Étapes
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void toggle(s)}>
            {s.isActive ? 'Désactiver' : 'Activer'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Nouvelle séquence
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={sequences ?? []}
        keyExtractor={(s) => s.id}
        loading={sequences === null}
        empty={{
          title: 'Aucune séquence',
          description: 'Créez une séquence pour planifier des relances automatiques.',
        }}
      />
      {createOpen && (
        <CreateSequenceModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}
      {selected && (
        <SequenceStepsModal
          sequence={selected}
          onClose={() => setSelected(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function CreateSequenceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await sequencesApi.create(token, { name });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Créer une séquence">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="sq-name" required>
            Nom de la séquence
          </Label>
          <Input
            id="sq-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Ex : Prospection froide 30 jours"
          />
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Créer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function SequenceStepsModal({
  sequence,
  onClose,
  onChanged,
}: {
  sequence: SequenceSummary;
  onClose: () => void;
  onChanged: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [detail, setDetail] = useState<SequenceSummary | null>(sequence);
  const [addOpen, setAddOpen] = useState(false);

  async function load() {
    try {
      setDetail(await sequencesApi.get(token, sequence.id));
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence.id]);

  async function remove(stepId: string) {
    const ok = await confirm({
      title: 'Supprimer cette étape ?',
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await sequencesApi.deleteStep(token, sequence.id, stepId);
      toast({ tone: 'success', message: 'Étape supprimée' });
      await load();
      onChanged();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const steps = detail?.steps ?? [];

  return (
    <Modal onClose={onClose} title={`Étapes : ${sequence.name}`} wide>
      <div className="space-y-4">
        {steps.length === 0 ? (
          <EmptyState
            variant="list"
            title="Aucune étape"
            description="Ajoutez au moins une étape pour rendre la séquence utilisable."
          />
        ) : (
          <ol className="space-y-2">
            {steps.map((step) => (
              <li
                key={step.id}
                className="flex items-center justify-between rounded-md border border-border-subtle bg-white p-3 shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge tone="accent">Étape {step.stepOrder}</Badge>
                    <span className="font-medium text-brand">{step.title}</span>
                  </div>
                  <div className="mt-1 text-xs text-text-muted">
                    Type : {step.eventType} · Délai : J+{step.delayDays}
                    {step.emailTemplate ? ` · Template : ${step.emailTemplate.name}` : ''}
                  </div>
                  {step.description && (
                    <div className="mt-1 text-xs text-text-muted">{step.description}</div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={() => void remove(step.id)}
                >
                  Supprimer
                </Button>
              </li>
            ))}
          </ol>
        )}
        <div className="flex justify-end">
          <Button
            variant="primary"
            leadingIcon={<Plus className="h-4 w-4" />}
            onClick={() => setAddOpen(true)}
          >
            Ajouter une étape
          </Button>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Fermer
          </Button>
        </ModalFooter>
        {addOpen && (
          <AddStepModal
            sequenceId={sequence.id}
            nextOrder={(steps[steps.length - 1]?.stepOrder ?? 0) + 1}
            onClose={() => setAddOpen(false)}
            onAdded={() => {
              setAddOpen(false);
              void load();
              onChanged();
            }}
          />
        )}
      </div>
    </Modal>
  );
}

function AddStepModal({
  sequenceId,
  nextOrder,
  onClose,
  onAdded,
}: {
  sequenceId: string;
  nextOrder: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [stepOrder, setStepOrder] = useState<number>(nextOrder);
  const [delayDays, setDelayDays] = useState<number>(0);
  const [eventType, setEventType] = useState<SequenceStepType>('task');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailTemplateId, setEmailTemplateId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const list = await configApi.listEmailTemplates(token);
        setEmailTemplates(list.filter((t) => t.isActive));
      } catch {
        setEmailTemplates([]);
      }
    })();
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await sequencesApi.createStep(token, sequenceId, {
        stepOrder,
        delayDays,
        eventType,
        title,
        description: description.trim() || undefined,
        emailTemplateId: eventType === 'email' ? emailTemplateId || null : undefined,
      });
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Ajouter une étape">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="st-order">Ordre</Label>
            <Input
              id="st-order"
              type="number"
              min={1}
              value={stepOrder}
              onChange={(e) => setStepOrder(Number(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="st-delay">Délai (jours)</Label>
            <Input
              id="st-delay"
              type="number"
              min={0}
              value={delayDays}
              onChange={(e) => setDelayDays(Number(e.target.value))}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="st-type">Type d&apos;action</Label>
          <Select
            id="st-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value as SequenceStepType)}
          >
            <option value="task">Tâche</option>
            <option value="call">Appel</option>
            <option value="email">Email</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="st-title" required>
            Titre
          </Label>
          <Input id="st-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="st-desc">Description</Label>
          <Textarea
            id="st-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {eventType === 'email' && (
          <div>
            <Label htmlFor="st-template">Template email</Label>
            <Select
              id="st-template"
              value={emailTemplateId}
              onChange={(e) => setEmailTemplateId(e.target.value)}
            >
              <option value="">-</option>
              {emailTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Ajouter
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
